/**
 * @file Custom native bridge types that are serialized/deserialized by the
 * bridge. These values will be last-in-line to be serialized when sending, and
 * will be first-in-line to be deserialized when receiving, to ensure that any
 * transformed values are opaque to depending packages.
 */

/**
 * A type that represents a custom value that is serialized/deserialized by the
 * native bridge.
 */
type NativeBridgeValue<TType extends string> = {
  __nativeBridgeValueType: TType
}

function isNativeBridgeValue<TType extends string>(value: unknown, type: TType): value is NativeBridgeValue<TType> {
  return (
    typeof value === 'object' &&
    value !== null &&
    '__nativeBridgeValueType' in value &&
    value.__nativeBridgeValueType === type
  )
}

/**
 * When serializing objects with `undefined` values into JSON, those values will
 * be omitted from the payload. To safely preserve `undefined` values across
 * serializations/deserializations, we use a custom type to represent
 * `undefined` when dispatching them across the bridge.
 */
function undefinedValue(): NativeBridgeValue<'undefined'> {
  return { __nativeBridgeValueType: 'undefined' }
}

function isUndefinedValue(value: unknown): value is NativeBridgeValue<'undefined'> {
  return isNativeBridgeValue(value, 'undefined')
}

export type { NativeBridgeValue }
export { undefinedValue, isUndefinedValue }
